import { Link } from 'react-router-dom';
import MenuBar from './Component/MenuBar';
import PageBanner from './Component/PageBanner';
import Sidebar from './Component/Sidebar';
import Footer from './Component/Footer';
import './AboutPage.css';
import './LegalPage.css';

const LAST_UPDATED = 'September 25, 2026';

// Section list used for the table of contents and anchor ids
const SECTIONS = [
    { id: 'acceptance', title: 'Acceptance of Terms' },
    { id: 'services', title: 'Our Services' },
    { id: 'accounts', title: 'Accounts and Registration' },
    { id: 'registrations-payments', title: 'Registrations, Payments and Refunds' },
    { id: 'conduct', title: 'Acceptable Use' },
    { id: 'content', title: 'Content and Intellectual Property' },
    { id: 'words-of-wisdom', title: 'Words of Wisdom Emails' },
    { id: 'third-party', title: 'Third-Party Services' },
    { id: 'disclaimer', title: 'Disclaimers' },
    { id: 'liability', title: 'Limitation of Liability' },
    { id: 'governing-law', title: 'Governing Law' },
    { id: 'changes', title: 'Changes to These Terms' },
    { id: 'contact', title: 'Contact Us' },
];

const TermsOfService = () => {
    return (
        <>
            <MenuBar />
            <Sidebar />
            <main className="yk-about-page yk-legal-page">
                {/* <!-- Page Banner Section --> */}
                <PageBanner title="Terms of Service" titleId="yk-terms-title" />
                {/* <!--End Banner Section --> */}

                <section className="yk-legal-content">
                    <div className="auto-container">
                        <p className="yk-legal-updated">Last updated: {LAST_UPDATED}</p>

                        <p className="yk-legal-intro">
                            These Terms of Service ("Terms") govern your use of yugalkunj.org and the related portals,
                            applications and programs (the "Services") provided by Radha Madhav Society, operating
                            Yugal Kunj – Radha Krishna Temple and Community Center ("Yugal Kunj", "we", "us").
                            Please read them carefully.
                        </p>

                        <nav className="yk-legal-toc" aria-labelledby="yk-terms-toc-heading">
                            <h2 id="yk-terms-toc-heading">Contents</h2>
                            <ol>
                                {SECTIONS.map((section) => (
                                    <li key={section.id}><a href={`#${section.id}`}>{section.title}</a></li>
                                ))}
                            </ol>
                        </nav>

                        <section id="acceptance" className="yk-legal-section" aria-labelledby="acceptance-heading">
                            <h2 id="acceptance-heading">1. Acceptance of Terms</h2>
                            <p>
                                By accessing or using the Services, registering for a program, becoming a member or
                                making a donation, you agree to these Terms and to our{' '}
                                <Link to="/USA/privacy-policy">Privacy Policy</Link>. If you do not agree, please do
                                not use the Services.
                            </p>
                        </section>

                        <section id="services" className="yk-legal-section" aria-labelledby="services-heading">
                            <h2 id="services-heading">2. Our Services</h2>
                            <p>
                                Yugal Kunj is a non-profit spiritual and community organization. Through the Services
                                we publish information about temple activities, provide online registration for
                                classes, camps, retreats and events, accept memberships and donations, coordinate
                                volunteers, offer event rentals and share weekly "Words of Wisdom" messages. We may
                                change, suspend or discontinue any part of the Services at any time.
                            </p>
                        </section>

                        <section id="accounts" className="yk-legal-section" aria-labelledby="accounts-heading">
                            <h2 id="accounts-heading">3. Accounts and Registration</h2>
                            <ul>
                                <li>You must provide accurate, current and complete information when registering.</li>
                                <li>You are responsible for keeping your login credentials confidential and for all activity under your account.</li>
                                <li>A parent or legal guardian must complete registration for participants under 18 and is responsible for the participant's conduct.</li>
                                <li>Notify us promptly at <a href="mailto:info@yugalkunj.org">info@yugalkunj.org</a> of any unauthorized use of your account.</li>
                            </ul>
                        </section>

                        <section id="registrations-payments" className="yk-legal-section" aria-labelledby="registrations-payments-heading">
                            <h2 id="registrations-payments-heading">4. Registrations, Payments and Refunds</h2>
                            <ul>
                                <li>Fees, schedules and capacity for each program are shown at the time of registration. A registration is confirmed only after payment is received, where a fee applies.</li>
                                <li>Payments are processed by third-party payment processors. You agree to their terms when paying.</li>
                                <li>Donations are voluntary contributions to Radha Madhav Society, a non-profit organization, and are generally non-refundable.</li>
                                <li>Refund and cancellation rules for a specific program (class, camp, retreat, rental) are stated on that program's page or confirmation email and take precedence over these general Terms. Where none is stated, refund requests are reviewed case by case; contact us before the program start date.</li>
                                <li>We may cancel or reschedule a program because of low enrollment, weather or other circumstances; in that case you will be offered a transfer or a refund of the program fee.</li>
                            </ul>
                        </section>

                        <section id="conduct" className="yk-legal-section" aria-labelledby="conduct-heading">
                            <h2 id="conduct-heading">5. Acceptable Use</h2>
                            <p>You agree not to:</p>
                            <ul>
                                <li>Use the Services for any unlawful purpose or in violation of these Terms.</li>
                                <li>Submit false, misleading or fraudulent information or payments.</li>
                                <li>Attempt to gain unauthorized access to any system, account or data, or interfere with the operation of the Services.</li>
                                <li>Copy, scrape or redistribute content from the Services except as permitted in Section 6.</li>
                                <li>Behave in a disrespectful, harassing or unsafe manner toward participants, volunteers, staff or visitors, whether online or on temple premises.</li>
                            </ul>
                            <p>We may suspend or terminate access to the Services for anyone who violates these Terms.</p>
                        </section>

                        <section id="content" className="yk-legal-section" aria-labelledby="content-heading">
                            <h2 id="content-heading">6. Content and Intellectual Property</h2>
                            <p>
                                All text, lectures, videos, images, logos and other content on the Services are owned by
                                Radha Madhav Society, Jagadguru Kripalu Parishat or their licensors and are protected by
                                copyright and trademark laws. You may view and share content for personal, non-commercial
                                devotional use with attribution. Any other use requires our prior written permission.
                            </p>
                            <p>
                                Photographs and video may be taken at programs and events for our records and
                                promotional use. If you do not wish to be photographed, please inform the program
                                coordinator.
                            </p>
                        </section>

                        <section id="words-of-wisdom" className="yk-legal-section" aria-labelledby="words-of-wisdom-heading">
                            <h2 id="words-of-wisdom-heading">7. Words of Wisdom Emails</h2>
                            <p>
                                By subscribing you agree to receive the weekly "Words of Wisdom" message and occasional
                                program announcements by email. You may unsubscribe at any time using the link in each
                                email or by contacting us. The messages are provided for spiritual inspiration and are
                                the copyrighted work of Sushri Siddheshvari Devi Ji and Radha Madhav Society; they may be
                                shared freely for non-commercial purposes with attribution.
                            </p>
                        </section>

                        <section id="third-party" className="yk-legal-section" aria-labelledby="third-party-heading">
                            <h2 id="third-party-heading">8. Third-Party Services</h2>
                            <p>
                                The Services may link to or rely on third-party services such as payment processors,
                                video platforms, maps and social media. We are not responsible for the content,
                                availability or privacy practices of those services, which are governed by their own
                                terms.
                            </p>
                        </section>

                        <section id="disclaimer" className="yk-legal-section" aria-labelledby="disclaimer-heading">
                            <h2 id="disclaimer-heading">9. Disclaimers</h2>
                            <p>
                                The Services and all content are provided "as is" and "as available" without warranties
                                of any kind, express or implied. We do not warrant that the Services will be
                                uninterrupted, error-free or free of harmful components. Participation in physical
                                activities, camps and retreats involves inherent risks; participants (or their guardians)
                                accept those risks and may be asked to sign a separate waiver.
                            </p>
                        </section>

                        <section id="liability" className="yk-legal-section" aria-labelledby="liability-heading">
                            <h2 id="liability-heading">10. Limitation of Liability</h2>
                            <p>
                                To the fullest extent permitted by law, Radha Madhav Society, Yugal Kunj and their
                                directors, volunteers and staff are not liable for any indirect, incidental, special or
                                consequential damages arising out of your use of the Services or participation in
                                programs. Our total liability for any claim relating to the Services shall not exceed
                                the amount you paid to us for the specific program giving rise to the claim.
                            </p>
                        </section>

                        <section id="governing-law" className="yk-legal-section" aria-labelledby="governing-law-heading">
                            <h2 id="governing-law-heading">11. Governing Law</h2>
                            <p>
                                These Terms are governed by the laws of the State of Georgia, USA, without regard to its
                                conflict-of-law rules. Any dispute shall be brought in the state or federal courts located
                                in Gwinnett County, Georgia.
                            </p>
                        </section>

                        <section id="changes" className="yk-legal-section" aria-labelledby="changes-heading">
                            <h2 id="changes-heading">12. Changes to These Terms</h2>
                            <p>
                                We may update these Terms from time to time. The "Last updated" date at the top shows
                                when they were last revised. Continued use of the Services after a change means you
                                accept the updated Terms.
                            </p>
                        </section>

                        <section id="contact" className="yk-legal-section" aria-labelledby="contact-heading">
                            <h2 id="contact-heading">13. Contact Us</h2>
                            <div className="yk-legal-contact">
                                <address>
                                    Radha Madhav Society – Yugal Kunj<br />
                                    2769 Duluth Hwy, Duluth, GA 30096, USA
                                </address>
                                <ul>
                                    <li>Email: <a href="mailto:info@yugalkunj.org">info@yugalkunj.org</a></li>
                                    <li>Phone: <a href="tel:678-920-6669">678-920-6669</a></li>
                                </ul>
                            </div>
                        </section>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
};

export default TermsOfService;
